// DocsPage — public documentation landing page.
// Hub for the 4 sub-pages.
import { Link } from "react-router-dom";
import {
    ArrowRight, BookOpen, Cpu, ShieldCheck, Target,
} from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { buildBreadcrumbListSchema } from "@/lib/schema";

const CARDS = [
    {
        to: "/docs/features",
        label: "What UAVSimNet does",
        summary:
            "Capability tour across airframes, environment fidelity, RF line-of-sight, threat modelling, guidance laws, engagement / lethality, and reviewer sign-off.",
        icon: <Target size={18} />,
        testId: "docs-card-features",
    },
    {
        to: "/docs/how-it-works",
        label: "How it works",
        summary:
            "Deterministic Monte Carlo, JSBSim-backed dynamics, the validation gate, the immutable parameter-provenance snapshot, and the reviewer workflow — told at the capability level.",
        icon: <Cpu size={18} />,
        testId: "docs-card-how-it-works",
    },
    {
        to: "/docs/evidence",
        label: "Evidence & credibility",
        summary:
            "Validated-vs-provisional model registry, citation support, bit-exact reproducibility for kinematic runs, and the reviewer chain that underwrites every claim.",
        icon: <ShieldCheck size={18} />,
        testId: "docs-card-evidence",
    },
    {
        to: "/docs/use-cases",
        label: "Use cases",
        summary:
            "Three counter-UAS scenarios told as narrative: urban-canyon net capture, contested-weather salvo engagement, and group-1 swarm versus a kinetic interceptor.",
        icon: <BookOpen size={18} />,
        testId: "docs-card-use-cases",
    },
];

export default function DocsPage() {
    return (
        <>
            <SeoHead
                path="/docs"
                schemaJsonLd={[
                    buildBreadcrumbListSchema({ path: "/docs", label: "Docs" }),
                ]}
            />
            <div data-testid="docs-page">
                <section className="section" style={{ paddingTop: 64 }}>
                    <div className="section__inner">
                        <span className="section__eyebrow">// DOCUMENTATION</span>
                        <h1 className="section__heading">UAVSimNet documentation</h1>
                        <p className="section__lede">
                            Public, capability-level documentation for counter-UAS
                            engineering, procurement, and operator-rehearsal teams.
                            Four short chapters — what the platform does, how it
                            works, the evidence behind its claims, and the use
                            cases it is built for.
                        </p>

                        <div
                            style={{
                                marginTop: 40,
                                display: "grid",
                                gridTemplateColumns:
                                    "repeat(auto-fit, minmax(260px, 1fr))",
                                gap: 18,
                            }}
                            data-testid="docs-cards"
                        >
                            {CARDS.map((c) => (
                                <Link
                                    key={c.to}
                                    to={c.to}
                                    className="cap-card"
                                    data-testid={c.testId}
                                    style={{ display: "block" }}
                                >
                                    <div className="cap-card__icon">{c.icon}</div>
                                    <div
                                        style={{
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                            color: "var(--ux-text-1)",
                                            fontSize: 14,
                                            marginBottom: 8,
                                        }}
                                    >
                                        {c.label}
                                    </div>
                                    <p
                                        style={{
                                            color: "var(--ux-text-3)",
                                            fontSize: 13,
                                            lineHeight: 1.55,
                                            margin: 0,
                                        }}
                                    >
                                        {c.summary}
                                    </p>
                                    <div
                                        style={{
                                            marginTop: 14,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            color: "var(--ux-accent)",
                                            fontSize: 12,
                                            fontFamily:
                                                "'JetBrains Mono', monospace",
                                        }}
                                    >
                                        Read <ArrowRight size={12} />
                                    </div>
                                </Link>
                            ))}
                        </div>

                        <div
                            style={{
                                marginTop: 48,
                                padding: "16px 20px",
                                borderRadius: 8,
                                border: "1px solid var(--ux-border-1)",
                                background: "var(--ux-bg-soft, #11181f)",
                                color: "var(--ux-text-3)",
                                fontSize: 12.5,
                                fontFamily: "'JetBrains Mono', monospace",
                                lineHeight: 1.6,
                            }}
                            data-testid="docs-retired-note"
                        >
                            The previous single-file HTML system overview has been
                            retired. These four chapters supersede it. Links to
                            <code style={{ margin: "0 4px" }}>/guide</code>
                            or{" "}
                            <code style={{ margin: "0 4px" }}>
                                /docs/system-overview
                            </code>{" "}
                            now redirect here.
                        </div>
                    </div>
                </section>
            </div>
        </>
    );
}
